// src/components/tabs/ItemsTab.tsx

import React from 'react';
import {
  Table,
  Input,
  Button,
  Empty,
  Tag,
  Tooltip,
  Image,
} from 'antd';
import {
  SearchOutlined,
  DeleteOutlined,
  PictureOutlined,
  DatabaseOutlined,
  FileTextOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { StorageItem, ItemCategory } from '../types/storage.types';
import { formatBytes, getCategoryColor, getCategoryLabel } from '../utils/storage.utils';

interface ItemsTabProps {
  items: StorageItem[];
  searchText: string;
  categoryFilter: string;
  selectedRowKeys: React.Key[];
  loadingThumbnails: Record<string, boolean>;
  onSearchChange: (value: string) => void;
  onCategoryFilterChange: (value: string) => void;
  onSelectionChange: (keys: React.Key[]) => void;
  onDeleteSelected: () => void;
  onLoadThumbnail: (key: string) => void;
}

export const ItemsTab: React.FC<ItemsTabProps> = ({
  items,
  searchText,
  categoryFilter,
  selectedRowKeys,
  loadingThumbnails,
  onSearchChange,
  onCategoryFilterChange,
  onSelectionChange,
  onDeleteSelected,
  onLoadThumbnail,
}) => {
  const getIconForCategory = (category: ItemCategory) => {
    const iconMap = {
      photo: <PictureOutlined style={{ color: "#52c41a" }} />,
      workflow: <FileTextOutlined style={{ color: "#fa8c16" }} />,
      sync: <SyncOutlined style={{ color: "#722ed1" }} />,
      other: <DatabaseOutlined style={{ color: "#666" }} />,
    };
    return iconMap[category];
  };

  const columns: ColumnsType<StorageItem> = [
    {
      title: "Vista Previa",
      key: "preview",
      width: 120,
      render: (_, record) => {
        if (record.category === "photo") {
          if (record.url) {
            return (
                <Image
                    src={record.url}
                    alt={record.key}
                    style={{ maxWidth: 80, maxHeight: 80, objectFit: "contain" }}
                />
            );
          } else {
            return (
              <Button
                type="default"
                size="small"
                onClick={() => onLoadThumbnail(record.key)}
                loading={loadingThumbnails[record.key]}
                icon={<PictureOutlined />}
              >
                Ver
              </Button>
            );
          }
        } else {
          return (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {getIconForCategory(record.category)}
              <Tag color="default" style={{ fontSize: "10px" }}>
                {record.type.split("/")[0]}
              </Tag>
            </div>
          );
        }
      },
    },
    {
      title: "ID",
      dataIndex: "key",
      key: "key",
      ellipsis: true,
      width: 200,
      render: (text) => (
        <Tooltip title={text} placement="topLeft">
          <span style={{ fontFamily: "monospace", fontSize: "11px" }}>
            {text}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Categoría",
      dataIndex: "category",
      key: "category",
      width: 100,
      filters: [
        { text: "Fotos", value: "photo" },
        { text: "Workflows", value: "workflow" },
        { text: "Sincronización", value: "sync" },
        { text: "Otros", value: "other" },
      ],
      onFilter: (value, record) => record.category === value,
      render: (category: ItemCategory, record) => (
        <Tag color={getCategoryColor(category)}>
          {getCategoryLabel(category)}
          {record.status && ` (${record.status})`}
        </Tag>
      ),
    },
    {
      title: "Tamaño",
      dataIndex: "size",
      key: "size",
      width: 100,
      sorter: (a, b) => a.size - b.size,
      render: (size) => formatBytes(size),
    },
    {
      title: "Última Modificación",
      dataIndex: "lastModified",
      key: "lastModified",
      width: 150,
      render: (text) => <span style={{ fontSize: "11px" }}>{text}</span>,
    },
    {
      title: "Acciones",
      key: "action",
      width: 80,
      render: (_, record) => (
        <Button
          type="text"
          danger
          size="small"
          icon={<DeleteOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            onSelectionChange([record.key]);
            onDeleteSelected();
          }}
        />
      ),
    },
  ];

  const filteredItems = items.filter((item) => {
    const matchesSearch = !searchText || 
      item.key.toLowerCase().includes(searchText.toLowerCase()) ||
      item.type.toLowerCase().includes(searchText.toLowerCase());
    
    const matchesCategory = categoryFilter === "all" || item.category === categoryFilter;
    
    return matchesSearch && matchesCategory;
  });

  return (
    <>
      {/* Controles de búsqueda y filtros */}
      <div
        style={{
          marginBottom: 16,
          display: "flex",
          gap: 16,
          alignItems: "center",
        }}
      >
        <Input
          placeholder="Buscar por ID o tipo..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => onSearchChange(e.target.value)}
          style={{ width: 300 }}
          allowClear
        />

        <select
          value={categoryFilter}
          onChange={(e) => onCategoryFilterChange(e.target.value)}
          style={{
            padding: "4px 8px",
            borderRadius: "6px",
            border: "1px solid #d9d9d9",
          }}
        >
          <option value="all">Todas las categorías</option>
          <option value="photo">
            Fotos ({items.filter((i) => i.category === "photo").length})
          </option>
          <option value="workflow">
            Workflows ({items.filter((i) => i.category === "workflow").length})
          </option>
          <option value="sync">
            Sincronización ({items.filter((i) => i.category === "sync").length})
          </option>
        </select>

        {selectedRowKeys.length > 0 && (
          <Button
            type="primary"
            danger
            icon={<DeleteOutlined />}
            onClick={onDeleteSelected}
          >
            Eliminar ({selectedRowKeys.length})
          </Button>
        )}
      </div>

      {/* Tabla de elementos */}
      {filteredItems.length > 0 ? (
        <Table
          rowSelection={{
            selectedRowKeys,
            onChange: onSelectionChange,
          }}
          columns={columns}
          dataSource={filteredItems}
          rowKey="key"
          size="small"
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (total) => `Total: ${total} elementos`,
          }}
          scroll={{ y: 400 }}
        />
      ) : (
        <Empty
          description="No hay elementos en el almacenamiento"
          image={
            <DatabaseOutlined style={{ fontSize: 64, color: "#d9d9d9" }} />
          }
        />
      )}
    </>
  );
};